import { useState, useEffect, useRef } from 'preact/hooks';
import { io, Socket } from 'socket.io-client';

/**
 * Hook to get real-time notification count via Socket.IO
 * @returns {Object} Object containing the count and connection status
 */
export function useNotificationCount() {
  const [count, setCount] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_NOTIFICATIONS_URL || '';

    if (!baseUrl) {
      setError(new Error('Socket.IO URL not provided'));
      return;
    }

    // Create Socket.IO connection with automatic cookie handling
    const socket = io(baseUrl + '/notifications', {
      path: '/socket.io',
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      autoConnect: true,
      withCredentials: true,
    });

    socketRef.current = socket;

    // Connection opened
    socket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      console.log('Connected to notification service');

      // Subscribe to unread count updates (initial count is sent upon subscription)
      socket.emit('subscribe-unread-count');
    });

    // Listen for unread count updates from the backend
    socket.on('unread-count', (data) => {
      try {
        if (data && typeof data.count === 'number') {
          setCount(data.count);
        }
      } catch (err) {
        console.error('Error handling unread count:', err);
      }
    });

    // Legacy event handler for backward compatibility
    socket.on('notification_count', (data) => {
      try {
        if (data && typeof data.count === 'number') {
          setCount(data.count);
        }
      } catch (err) {
        console.error('Error handling notification count:', err);
      }
    });

    // Alternative event handlers for compatibility
    const alternativeEvents = [
      'notificationCount',
      'notification_update',
      'unreadCount',
      'count',
      'update',
    ];

    alternativeEvents.forEach((eventName) => {
      socket.on(eventName, (data) => {
        try {
          let extractedCount = null;

          if (typeof data === 'number') {
            extractedCount = data;
          } else if (data && typeof data.count === 'number') {
            extractedCount = data.count;
          } else if (data && typeof data.unreadCount === 'number') {
            extractedCount = data.unreadCount;
          } else if (data && Array.isArray(data)) {
            extractedCount = data.filter(
              (item) => item.unread || !item.read,
            ).length;
          }

          if (extractedCount !== null) {
            setCount(extractedCount);
          }
        } catch (err) {
          console.error(`Error handling ${eventName} event:`, err);
        }
      });
    });

    // Listen for notification messages
    socket.on('notifications', (data) => {
      try {
        if (data && Array.isArray(data.messages)) {
          const unreadCount = data.messages.filter(
            (msg: any) => !msg.read,
          ).length;
          setCount(unreadCount);
        }
      } catch (err) {
        console.error('Error handling notifications:', err);
      }
    });

    // Connection disconnected
    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('Disconnected from notification service:', reason);
    });

    // Connection error
    socket.on('connect_error', (err) => {
      setError(new Error(`Socket.IO connection error: ${err.message}`));
      setIsConnected(false);
      console.error('Socket.IO connection error:', err.message);

      // Log authentication-specific errors
      if (
        err.message.includes('Authentication') ||
        err.message.includes('token') ||
        err.message.includes('Unauthorized')
      ) {
        console.error(
          'Authentication error: JWT token may be missing or expired',
        );
      }
    });

    // Handle authentication errors from the server
    socket.on('error', (errorMessage) => {
      console.error('Server error:', errorMessage);
      setError(new Error(`Server error: ${errorMessage}`));
    });

    // Cleanup function
    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, []);

  // Function to manually reconnect if needed
  const reconnect = () => {
    if (socketRef.current) {
      if (socketRef.current.connected) {
        socketRef.current.disconnect();
      }
      socketRef.current.connect();
    } else {
      setIsConnected(false);
      setError(null);
    }
  };

  return {
    count,
    isConnected,
    error,
    reconnect,
  };
}
